import { TextField, Autocomplete, IconButton, Skeleton } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { useCallback, useMemo } from 'react';

const LoadingSkeleton = () => (
  <div className='flex flex-col gap-2 py-1'>
    <Skeleton variant='rounded' height={22} />
    <Skeleton variant='rounded' height={22} width='85%' />
    <Skeleton variant='rounded' height={22} width='70%' />
  </div>
);

export interface Option {
  label: string;
  data: unknown;
}

const getStableOptionKey = (option: string | Option): string => {
  if (typeof option === 'string') {
    return option;
  }
  const raw = option.data;
  if (typeof raw === 'string' && raw !== '') {
    return raw;
  }
  if (typeof raw === 'object' && raw !== null && 'id' in raw) {
    const id = (raw as { id: unknown }).id;
    if (typeof id === 'string' && id !== '') {
      return id;
    }
    if (typeof id === 'number' && !Number.isNaN(id)) {
      return String(id);
    }
  }
  return option.label;
};

export interface AutocompleteInputProps {
  name: string;
  placeholder: string;
  value?: Option | null;
  inputValue?: string;
  onChange: (value: Option | null) => void;
  onInputChange: (value: string, reason?: string) => void;
  onBlur?: () => void;
  options: Option[];
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  errorInfo?: string;
  autoFocus?: boolean;
  freeSolo?: boolean;
  showTypingLengthHint?: boolean;
  forcePopupIcon?: boolean;
}

const AutocompleteInput = ({
  name,
  placeholder,
  value,
  inputValue,
  onChange,
  onInputChange,
  onBlur,
  options,
  required = false,
  disabled = false,
  loading = false,
  error = false,
  errorInfo = '',
  autoFocus = false,
  freeSolo = false,
  showTypingLengthHint = true,
  forcePopupIcon,
}: AutocompleteInputProps) => {
  const handleClear = useCallback(() => {
    onChange(null);
    onInputChange('');
  }, [onChange, onInputChange]);

  const getOptionLabel = useCallback((option: string | Option) => {
    if (typeof option === 'string') return option;
    return option.label;
  }, []);

  const filterOptions = useCallback((x: Option[]) => x, []);

  const isOptionEqualToValue = useCallback(
    (option: Option, value: Option | null) => {
      if (!value) {
        return false;
      }
      return getStableOptionKey(option) === getStableOptionKey(value);
    },
    []
  );

  const displayPlaceholder = value
    ? placeholder
    : showTypingLengthHint
      ? `${placeholder} (escriba 3+ caracteres para buscar)`
      : placeholder;

  const mergedOptions = useMemo(() => {
    if (
      value &&
      !options.some(
        (option) => getStableOptionKey(option) === getStableOptionKey(value)
      )
    ) {
      return [value, ...options];
    }
    return options;
  }, [value, options]);

  return (
    <Autocomplete
      id={name}
      disabled={disabled}
      loading={loading}
      loadingText={<LoadingSkeleton />}
      // @ts-ignore
      value={value}
      {...(inputValue !== undefined && { inputValue })}
      options={mergedOptions}
      onChange={(_, newValue) => {
        if (freeSolo && typeof newValue === 'string') {
          onChange({ label: newValue, data: newValue });
        } else {
          onChange(newValue as Option | null);
        }
      }}
      onInputChange={(_, newValue, reason) => onInputChange(newValue, reason)}
      getOptionLabel={getOptionLabel}
      getOptionKey={getStableOptionKey}
      filterOptions={filterOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      freeSolo={freeSolo}
      {...(forcePopupIcon !== undefined ? { forcePopupIcon } : {})}
      renderInput={(params) => {
        const { InputProps, ...otherParams } = params;
        return (
          <TextField
            {...otherParams}
            label={displayPlaceholder}
            variant='outlined'
            required={required}
            disabled={disabled}
            error={error}
            helperText={errorInfo}
            autoFocus={autoFocus}
            fullWidth
            onBlur={onBlur}
            inputProps={{
              ...otherParams.inputProps,
              autoComplete: 'off',
            }}
            InputProps={{
              ...InputProps,
              endAdornment: (
                <>
                  {value && (
                    <IconButton
                      aria-label='clear'
                      onClick={handleClear}
                      edge='end'
                      disabled={disabled}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                  {InputProps.endAdornment}
                </>
              ),
            }}
          />
        );
      }}
      fullWidth
      disableClearable
    />
  );
};

export default AutocompleteInput;
